import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

export const TestimonialSlider = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'CEO, TechStart Solutions',
      rating: 5,
      text: 'Global Mark Registry made the entire trademark process seamless and stress-free. Their team was professional, responsive, and guided us through every step. Our trademark was approved in record time!'
    },
    {
      name: 'Michael Chen',
      role: 'Founder, Artisan Coffee Co.',
      rating: 5,
      text: 'I was worried about the complexity of trademark registration, but Global Mark Registry simplified everything. The comprehensive search report gave me peace of mind, and their case manager was always available.'
    },
    {
      name: 'Jessica Rodriguez',
      role: 'Creative Director, Bloom Studios',
      rating: 5,
      text: 'Outstanding service from start to finish! The team demonstrated deep expertise in trademark law and provided invaluable guidance. They protected our brand identity efficiently and affordably.'
    },
    {
      name: 'David Thompson',
      role: 'Owner, Urban Fitness Center',
      rating: 5,
      text: 'After struggling with a DIY trademark filing, I turned to Global Mark Registry for help. They quickly identified issues with my previous application and successfully refiled. Their attention to detail is unmatched.'
    },
    {
      name: 'Emily Watson',
      role: 'Founder, EcoWear Fashion',
      rating: 5,
      text: 'The Deluxe package was perfect for our growing brand. The comprehensive search prevented potential conflicts, and the ongoing monitoring service gives us continuous protection. Truly exceptional!'
    },
    {
      name: 'Robert Martinez',
      role: 'President, Martinez Law Firm',
      rating: 5,
      text: 'As an attorney myself, I appreciate quality legal work. Global Mark Registry exceeded my expectations with their thorough trademark search and flawless filing process. I recommend them to all my clients.'
    },
    {
      name: 'Amanda Foster',
      role: 'Entrepreneur, Foster Boutique',
      rating: 5,
      text: 'Starting my business was daunting, but Global Mark Registry made protecting my brand name easy. They explained everything clearly and handled all the paperwork. Now I can focus on growing my business worry-free.'
    },
    {
      name: 'James Cooper',
      role: 'CMO, Digital Innovations Inc.',
      rating: 5,
      text: 'We needed to trademark multiple brands quickly, and Global Mark Registry delivered. Their team managed all our applications simultaneously with precision. The best trademark service we\'ve ever used!'
    },
    {
      name: 'Linda Park',
      role: 'Designer, Park Creative Agency',
      rating: 5,
      text: 'Global Mark Registry protected not just my business name but also my logo designs. The process was surprisingly fast, and their attorneys were incredibly knowledgeable. I felt supported throughout the entire journey.'
    },
    {
      name: 'Christopher Hayes',
      role: 'Founder, Hayes Consulting Group',
      rating: 5,
      text: 'I\'ve used other trademark services before, but Global Mark Registry stands out for their personalized attention and expertise. They caught potential issues early and saved me thousands in potential legal fees.'
    },
    {
      name: 'Patricia Johnson',
      role: 'Owner, Johnson Home Goods',
      rating: 5,
      text: 'The team went above and beyond to ensure my trademark application was perfect. Their communication was excellent, and they kept me informed every step of the way. Highly professional service!'
    },
    {
      name: 'Kevin Anderson',
      role: 'CEO, Anderson Tech Solutions',
      rating: 5,
      text: 'Global Mark Registry registered our company trademark and helped us expand internationally. Their knowledge of trademark law across different jurisdictions is impressive. We trust them completely with our IP portfolio.'
    }
  ];

  useEffect(() => {
    if (!isPaused) {
      const timer = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % testimonials.length);
      }, 4000);
      return () => clearInterval(timer);
    }
  }, [isPaused, testimonials.length]);

  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (activeIndex + i) % testimonials.length;
      visible.push({ ...testimonials[index], originalIndex: index });
    }
    return visible;
  };

  const handlePrevious = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Desktop View - Auto-sliding with 3 visible */}
      <div 
        className="hidden md:block relative"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="grid grid-cols-3 gap-8">
          {getVisibleTestimonials().map((testimonial, index) => (
            <div
              key={`${testimonial.originalIndex}-${index}`}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 animate-fade-in"
            >
              <Quote className="w-10 h-10 text-yellow-400 mb-4" />
              <div className="flex space-x-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic min-h-[120px]">&ldquo;{testimonial.text}&rdquo;</p>
              <div className="border-t pt-4">
                <div className="font-bold text-gray-900">{testimonial.name}</div>
                <div className="text-sm text-gray-600">{testimonial.role}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={handlePrevious}
          className="absolute -left-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:bg-gray-100 transition-all z-10"
        >
          <ChevronLeft className="w-6 h-6 text-gray-700" />
        </button>
        <button
          onClick={handleNext}
          className="absolute -right-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:bg-gray-100 transition-all z-10"
        >
          <ChevronRight className="w-6 h-6 text-gray-700" />
        </button>

        {/* Progress Indicators */}
        <div className="flex justify-center space-x-2 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`h-2 rounded-full transition-all ${
                index === activeIndex ? 'bg-red-600 w-8' : 'bg-gray-300 w-2'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Mobile Slider */}
      <div className="md:hidden">
        {testimonials.map((testimonial, index) => (
          <div
            key={index}
            className={`bg-white rounded-xl p-8 shadow-lg transition-all duration-500 ${
              activeIndex === index ? 'block' : 'hidden'
            }`}
          >
            <Quote className="w-10 h-10 text-yellow-400 mb-4" />
            <div className="flex space-x-1 mb-4">
              {[...Array(testimonial.rating)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-gray-700 mb-6 italic">&ldquo;{testimonial.text}&rdquo;</p>
            <div className="border-t pt-4">
              <div className="font-bold text-gray-900">{testimonial.name}</div>
              <div className="text-sm text-gray-600">{testimonial.role}</div>
            </div>
          </div>
        ))}
        <div className="flex justify-center space-x-2 mt-6">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                activeIndex === index ? 'bg-red-600 w-8' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};